import Fuse from 'fuse.js'
import { useMemo } from 'react'

import type { UserDetails } from '../../../../../../types/user'

interface MemberSearchProps {
  invitedUsers?: { user: UserDetails }[]
  selectedUsers: { user: UserDetails }[]
  suggestedUsers?: { user: UserDetails }[]
  userInput: string
}
const getSortedMembers = (members: { user: UserDetails }[]) => {
  if (!members?.length) return members

  return [...members].sort((a, b) => {
    const aName = a.user?.name || a.user?.email || a.user?.phone
    const bName = b.user?.name || b.user?.email || b.user?.phone
    return bName && aName ? aName?.localeCompare(bName) : -1
  })
}

/**
 * This hook is used to search for members in the workspace.
 * It filters the members based on the user input.
 */
export const useMemberSearch = ({
  userInput,
  suggestedUsers = [],
  invitedUsers = [],
  selectedUsers
}: MemberSearchProps) => {
  const filteredSelectedUsers = selectedUsers.filter(
    (selected) =>
      !suggestedUsers?.some((suggested) => suggested.user.id === selected.user.id) &&
      !invitedUsers?.some((invited) => invited.user.id === selected.user.id)
  )

  return useMemo(() => {
    if (!userInput) {
      return [...suggestedUsers, ...invitedUsers, ...filteredSelectedUsers]
    }

    const fuse = new Fuse([...suggestedUsers, ...invitedUsers, ...filteredSelectedUsers], {
      keys: ['user.email', 'user.name', 'user.phone'],
      threshold: 0.5
    })

    return getSortedMembers(fuse.search(`=${userInput}`).map(({ item }) => item))
  }, [userInput, suggestedUsers, invitedUsers, filteredSelectedUsers])
}
